import React, { useState } from 'react';
import { 
  FaHome, FaCog, FaImages, FaQuoteLeft, FaEnvelope, 
  FaCalendarAlt, FaUsers, FaServicestack, FaBriefcase,
  FaStar, FaPhotoVideo, FaLink
} from 'react-icons/fa';
import { MdViewCarousel } from 'react-icons/md';

import HeroSections from './WebsiteContent/HeroSections';
import HeroGallery from './WebsiteContent/HeroGallery';
import Banners from './WebsiteContent/Banners';
import Services from './WebsiteContent/Services';
import Portfolio from './WebsiteContent/Portfolio';
import Testimonials from './WebsiteContent/Testimonials';
import TeamMembers from './WebsiteContent/TeamMembers';
import ContentBlocks from './WebsiteContent/ContentBlocks';
import StudioMedia from './WebsiteContent/StudioMedia';
import Consultations from './WebsiteContent/Consultations';
import ContactMessages from './WebsiteContent/ContactMessages';
import QuoteRequests from './WebsiteContent/QuoteRequests';
import Settings from './WebsiteContent/Settings';
import { usePermission } from '../../../hooks/usePermission';

const WebsiteContent = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const { can } = usePermission();

  // Define all website content sections
  const contentSections = [
    { id: 'overview', label: 'Overview', icon: <FaHome />, permission: null },
    { id: 'hero-sections', label: 'Hero Sections', icon: <MdViewCarousel />, permission: 'content.view' },
    { id: 'hero-gallery', label: 'Hero Gallery', icon: <FaImages />, permission: 'content.view' },
    { id: 'banners', label: 'Banners', icon: <FaPhotoVideo />, permission: 'content.view' },
    { id: 'services', label: 'Services', icon: <FaServicestack />, permission: 'content.view' },
    { id: 'portfolio', label: 'Portfolio', icon: <FaBriefcase />, permission: 'content.view' },
    { id: 'testimonials', label: 'Testimonials', icon: <FaQuoteLeft />, permission: 'content.view' },
    { id: 'team-members', label: 'Team Members', icon: <FaUsers />, permission: 'content.view' },
    { id: 'content-blocks', label: 'Content Blocks', icon: <FaImages />, permission: 'content.view' },
    { id: 'studio-media', label: 'Studio Media', icon: <FaPhotoVideo />, permission: 'content.view' },
    { id: 'consultations', label: 'Consultations', icon: <FaCalendarAlt />, permission: 'submissions.view' },
    { id: 'contact-messages', label: 'Contact Messages', icon: <FaEnvelope />, permission: 'submissions.view' },
    { id: 'quote-requests', label: 'Quote Requests', icon: <FaStar />, permission: 'submissions.view' },
    { id: 'settings', label: 'Site Settings', icon: <FaCog />, permission: 'settings.view' }
  ];

  // Filter sections based on permissions
  const sections = contentSections.filter(section => {
    if (!section.permission) return true;
    return can(section.permission);
  });

  const renderContent = () => {
    switch (activeSection) {
      case 'hero-sections':
        return can('content.view') ? <HeroSections /> : <AccessDenied />;
      case 'hero-gallery':
        return can('content.view') ? <HeroGallery /> : <AccessDenied />;
      case 'banners':
        return can('content.view') ? <Banners /> : <AccessDenied />;
      case 'services':
        return can('content.view') ? <Services /> : <AccessDenied />;
      case 'portfolio':
        return can('content.view') ? <Portfolio /> : <AccessDenied />;
      case 'testimonials':
        return can('content.view') ? <Testimonials /> : <AccessDenied />;
      case 'team-members':
        return can('content.view') ? <TeamMembers /> : <AccessDenied />;
      case 'content-blocks':
        return can('content.view') ? <ContentBlocks /> : <AccessDenied />;
      case 'studio-media':
        return can('content.view') ? <StudioMedia /> : <AccessDenied />;
      case 'consultations':
        return can('submissions.view') ? <Consultations /> : <AccessDenied />;
      case 'contact-messages':
        return can('submissions.view') ? <ContactMessages /> : <AccessDenied />;
      case 'quote-requests':
        return can('submissions.view') ? <QuoteRequests /> : <AccessDenied />;
      case 'settings':
        return can('settings.view') ? <Settings /> : <AccessDenied />;
      default:
        return <ContentOverview sections={sections} setActiveSection={setActiveSection} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Website Content Management</h1>
            <p className="text-purple-100">Manage all your website content from one place</p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
              <span className="text-white font-medium">🎨 Content Hub</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-2">
        <div className="flex flex-wrap gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                activeSection === section.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              <span className="text-lg">{section.icon}</span>
              <span className="font-medium hidden sm:inline">{section.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="animate-fadeIn">
        {renderContent()}
      </div>
    </div>
  );
};

// Content Overview Component
const ContentOverview = ({ sections, setActiveSection }) => {
  const contentStats = [
    { label: 'Hero Sections', value: '5', icon: <MdViewCarousel />, color: 'from-blue-500 to-blue-600' },
    { label: 'Services', value: '8', icon: <FaServicestack />, color: 'from-green-500 to-green-600' },
    { label: 'Portfolio Items', value: '24', icon: <FaBriefcase />, color: 'from-purple-500 to-purple-600' },
    { label: 'Testimonials', value: '12', icon: <FaQuoteLeft />, color: 'from-orange-500 to-orange-600' },
    { label: 'Team Members', value: '6', icon: <FaUsers />, color: 'from-pink-500 to-pink-600' },
    { label: 'Media Files', value: '45', icon: <FaPhotoVideo />, color: 'from-indigo-500 to-indigo-600' }
  ];

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full" />
          Content Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contentStats.map((stat, index) => (
            <div 
              key={stat.label}
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6 animate-slideUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm font-medium">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{stat.value}</p>
                </div>
                <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                  <span className="text-2xl text-white">{stat.icon}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full" />
          Content Management
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.filter(s => s.id !== 'overview').map((section, index) => (
            <div 
              key={section.id}
              className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border border-slate-200/50 animate-slideUp"
              onClick={() => setActiveSection(section.id)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-pink-600/0 group-hover:from-purple-500/10 group-hover:to-pink-600/10 transition-all duration-500" />
              
              <div className="relative p-6 flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <span className="text-2xl text-white">{section.icon}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-800 group-hover:text-purple-600 transition-colors">
                    {section.label}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Manage content</p>
                </div>
                <div className="text-slate-400 group-hover:text-purple-500 group-hover:translate-x-1 transition-all duration-300">
                  →
                </div>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] animate-scaleIn">
    <div className="relative">
      <div className="absolute inset-0 bg-red-500/20 blur-3xl rounded-full animate-pulse" />
      <div className="relative w-32 h-32 bg-gradient-to-br from-red-500 to-red-700 rounded-3xl flex items-center justify-center shadow-2xl">
        <FaLink className="text-6xl text-white" />
      </div>
    </div>
    <h3 className="text-2xl font-bold text-slate-800 mt-8 mb-2">Access Denied</h3>
    <p className="text-slate-500 text-center max-w-md">
      You do not have permission to view this content section. Please contact your administrator for access.
    </p>
  </div>
);

export default WebsiteContent;